import { useState } from 'react';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { PageInfoHeading } from '@/components/Header/Page/PageInfoHeading';
import {
  useGetAllCompanies,
  useUpdateManyCompanies,
} from '@/hooks/company.hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { CompanyTableView } from '../components/CompanyTableView';
import { TCompanySchema } from '@/schemas/company/company.schema';
import { CompanyTableHeaders } from '../components/CompanyTableHeaders';
import { CompanyTableData } from '../components/CompanyTableData';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import Modal from '@/components/Modal';
import { useDeactivateField } from '@/hooks/deactivate-field.hooks';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import { ImportIcon } from '@/components/SvgIcons/ImportIcon';
import { useGetGroupById } from '@/hooks/group.hooks';
import Loader from '@/components/Loading/Loader';
import { useUserRole } from '@/hooks/auth.hooks';
import { WarningMessage } from '@/components/Message/WarningMessage';

export const CompanyListPage = () => {
  const { t } = useTranslation('company');
  const { isAdmin, isGroupManager } = useUserRole();
  const navigate = useNavigate();
  const { groupId } = useParams();

  const [totalActive, setTotalActive] = useState<number>(0);

  const { data: group, isLoading: isLoadingGroup } = useGetGroupById(groupId);

  /**
   * === Deactivate Data section ===
   */
  const {
    numberOfItemsToDeactivate,
    pendingDeactivate,
    handleDeactivate,
    onConfirmDeactivate,
  } = useDeactivateField<TCompanySchema>({
    t,
    linkedFieldNames: ['branches'],
    useUpdateData: useUpdateManyCompanies,
  });
  /**
   * === END OF Deactivate Data section ===
   */

  const onGetTotalActive = (total: number) => {
    setTotalActive(total);
  };

  return (
    <>
      <PageHeader
        backToLink={groupId ? '/groups' : undefined}
        title={t('common:companies')}
        totalItems={totalActive}
        headingElement={
          <PageInfoHeading
            text={
              group ? (
                <>
                  {t('common:companiesLinkedToTheGroup')}{' '}
                  <span className="font-medium">{group?.data?.name}</span>
                </>
              ) : (
                t('common:detailsRelatedToCurrentSection')
              )
            }
          />
        }
        actionListElement={
          <>
            {(isAdmin || isGroupManager) && (
              <>
                <HeadingButton
                  text={t('common:importAList')}
                  leftIcon={
                    <ImportIcon className="-mt-[1.5px] w-[15.5px] h-[17px] [&>path]:fill-light-inverse dark-mode:[&>path]:fill-gray-600" />
                  }
                  onClick={() => navigate('#')}
                />
                <HeadingButton
                  text={t('common:addCompany')}
                  leftIcon={
                    <i className="-mt-[1.5px] ki-outline ki-plus-circle !text-[19.4px] text-light-inverse dark-mode:text-gray-600" />
                  }
                  onClick={() => navigate('/company/create')}
                />
              </>
            )}
          </>
        }
      />
      <div>
        <CompanyTableView<TCompanySchema>
          t={t}
          useGetAllData={useGetAllCompanies}
          onGetTotalActive={onGetTotalActive}
          tableHeaders={CompanyTableHeaders}
          tableData={CompanyTableData}
          pendingDeactivate={pendingDeactivate}
          onDeactivate={handleDeactivate}
        />
      </div>
      <Modal
        id="information-modal"
        title={t('common:information')}
        centerY
        showCancelBtn={false}
        showSecondBtn={true}
        secondBtnLabel="Ok"
        size="small"
      >
        <WarningMessage message={t('common:unableToDeactivateLinkedItem')} />
      </Modal>
      <ConfirmationModal
        id="deactivation-confirm-modal"
        title={t('common:confirmation')}
        description={
          numberOfItemsToDeactivate > 1 ? (
            <Trans i18nKey="common:confirmDeactivateItems" />
          ) : (
            t('common:confirmDeactivateItem')
          )
        }
        onConfirmLabel={t('common:deactivate')}
        onConfirm={onConfirmDeactivate}
      />
      {isLoadingGroup && <Loader />}
    </>
  );
};
